import {
    Box,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Switch,
    TextField,
    useTheme
} from '@mui/material';
import { fontOptions } from 'consts';
import { ChangeEvent } from 'react';
import { QuizFontStyles } from 'types';

interface Props {
    fontStyles?: QuizFontStyles;
    onFontStylesChange: (fontStyles: QuizFontStyles) => void;
}

export const EditFontStyles = (props: Props) => {
    const theme = useTheme();
    const isItalic = props.fontStyles?.fontStyle === 'italic';
    const onFontChange = (event: SelectChangeEvent<string>) => {
        props.onFontStylesChange({
            ...props.fontStyles,
            fontFamily: event.target.value ? event.target.value : undefined
        });
    };

    const onFontSizeChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const newFontSize = event.target.value ? parseInt(event.target.value) : undefined;
        const defaultSize = getCurrentFontSize()
        if (defaultSize === newFontSize && 
        (!props.fontStyles?.fontSize || props.fontStyles?.fontSize === defaultSize)) {
            return;
        }
        props.onFontStylesChange({
            ...props.fontStyles,
            fontSize: newFontSize
        });
    };

    const onLineHeightChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        props.onFontStylesChange({
            ...props.fontStyles,
            lineHeight: event.target.value ? parseFloat(event.target.value) : undefined
        });
    };

    const onFontWeightChange = (event: SelectChangeEvent<string>) => {
        props.onFontStylesChange({
            ...props.fontStyles,
            fontWeight: event.target.value
                ? (event.target.value as
                      | 'bold'
                      | 'bolder'
                      | 'normal'
                      | 'lighter'
                      | 'revert'
                      | 'inherit'
                      | 900
                      | 800
                      | 700
                      | 600
                      | 500
                      | 400
                      | 300
                      | 200
                      | 100)
                : undefined
        });
    };

    const onItalicSwitch = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
        props.onFontStylesChange({
            ...props.fontStyles,
            fontStyle: checked ? 'italic' : undefined
        });
    };

    /**
     * Based on app CSS body font-size - calc(10px + 2vmin)
     * @returns
     */
    const getCurrentFontSize = (): number => {
        const minDimention = window.innerWidth > window.innerHeight ? window.innerHeight : window.innerWidth;
        return Math.round(10 + 2 * (minDimention / 100));
    };

    return (
        <Box maxWidth={'80%'} sx={{ display: 'flex', alignItems: 'center', margin: '0 20px', flexGrow: 1 }}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Box sx={props.fontStyles ? { ...props.fontStyles } : undefined} className="font-style-example-box">
                        <p>This is some sample text...</p>
                        <p>regional characters šđčćžŠĐČĆŽ...</p>
                        <p>symbols $#%&€@:-)</p>
                    </Box>
                </Grid>
                <Grid item xs={4}>
                    <FormControl fullWidth>
                        <InputLabel id="lbl-font">Font</InputLabel>
                        <Select
                            labelId="lbl-font"
                            id="ddl-font"
                            value={props.fontStyles?.fontFamily ?? ''} // Note: MUI Select has an issue with undefined/null values so we need to set and empty string here when genre is not defined. https://github.com/mui/material-ui/issues/4991
                            label="Font"
                            autoWidth
                            onChange={onFontChange}
                        >
                            <MenuItem value={''}>
                                <em>Unset</em>
                            </MenuItem>
                            {fontOptions.map((fo, index) => (
                                <MenuItem key={`font-${index}`} value={fo.value}>
                                    <span style={{fontFamily: fo.value}}>{fo.text}</span>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={4}>
                    <TextField
                        label="Font Size"
                        id="txt-font-size"
                        type="number"
                        inputProps={{
                            min: 10,
                            max: 1000
                        }}
                        value={props.fontStyles?.fontSize ? props.fontStyles?.fontSize : getCurrentFontSize()}
                        onChange={onFontSizeChange}
                    />
                </Grid>
                <Grid item xs={4}>
                    <TextField
                        label="Line Height"
                        id="txt-line-height"
                        type="number"
                        inputProps={{
                            min: 0,
                            max: 10,
                            step: 0.01
                        }}
                        value={props.fontStyles?.lineHeight ? props.fontStyles?.lineHeight : theme.typography.body1.lineHeight}
                        onChange={onLineHeightChange}
                    />
                </Grid>
                <Grid item xs={6}>
                    <FormControl fullWidth>
                        <InputLabel id="lbl-font-weight">Font Weight</InputLabel>
                        <Select
                            labelId="lbl-font-weight"
                            id="ddl-font-weight"
                            value={props.fontStyles?.fontWeight?.toString() ?? ''} // Note: MUI Select has an issue with undefined/null values so we need to set and empty string here when genre is not defined. https://github.com/mui/material-ui/issues/4991
                            label="Font weight"
                            autoWidth
                            onChange={onFontWeightChange}
                        >
                            <MenuItem value={''}>
                                <em>Unset</em>
                            </MenuItem>
                            <MenuItem value={'bold'}>
                                <span>Bold</span>
                            </MenuItem>
                            <MenuItem value={'normal'}>
                                <span>Normal</span>
                            </MenuItem>
                            <MenuItem value={'lighter'}>
                                <span>Lighter</span>
                            </MenuItem>
                            <MenuItem value={100}>
                                <span>100</span>
                            </MenuItem>
                            <MenuItem value={200}>
                                <span>200</span>
                            </MenuItem>
                            <MenuItem value={300}>
                                <span>300</span>
                            </MenuItem>
                            <MenuItem value={400}>
                                <span>400</span>
                            </MenuItem>
                            <MenuItem value={500}>
                                <span>500</span>
                            </MenuItem>
                            <MenuItem value={600}>
                                <span>600</span>
                            </MenuItem>
                            <MenuItem value={700}>
                                <span>700</span>
                            </MenuItem>
                            <MenuItem value={800}>
                                <span>800</span>
                            </MenuItem>
                            <MenuItem value={900}>
                                <span>900</span>
                            </MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={6}>
                    <FormControlLabel
                        value={isItalic}
                        control={<Switch value={isItalic} color="primary" onChange={onItalicSwitch} />}
                        label="Italic"
                        labelPlacement="start"
                    />
                </Grid>
            </Grid>
        </Box>
    );
};
